import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

// Utility functions for dynamic theme handling
const createGradient = (color1, color2, direction = '135deg') => 
  `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`;

const createShadow = (color, opacity = 0.3, size = 'medium') => {
  const sizes = {
    small: '0px 2px 8px',
    medium: '0px 4px 12px',
    large: '0px 8px 25px',
  };
  return `${sizes[size]} ${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
};

// Enhanced ToggleButtonGroup with consistent styling
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme, buttonType }) => {
  const getButtonConfig = () => {
    switch (buttonType) {
      case 'action':
        return {
          '& .MuiToggleButton-root': {
            borderRadius: 0,
            padding: theme.spacing(1, 2),
            fontSize: '0.875rem',
            minWidth: 80,
            border: '1px solid',
            borderColor: theme.palette.divider,
            '&:hover': {
              boxShadow: createShadow(theme.palette.primary.main, 0.2, 'small'),
              transform: 'translateY(-1px)',
            },
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        };
      case 'form':
        return {
          '& .MuiToggleButton-root': {
            borderRadius: 0,
            padding: theme.spacing(0.75, 1.5),
            fontSize: '0.8rem',
            minWidth: 70,
            border: '1px solid',
            borderColor: theme.palette.divider,
            '&:hover': {
              boxShadow: createShadow(theme.palette.primary.main, 0.15, 'small'),
            },
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        };
      default:
        return {
          '& .MuiToggleButton-root': {
            borderRadius: 0,
            padding: theme.spacing(1, 2),
            fontSize: '0.875rem',
            minWidth: 80,
            border: '1px solid',
            borderColor: theme.palette.divider,
            '&:hover': {
              boxShadow: createShadow(theme.palette.primary.main, 0.2, 'small'),
              transform: 'translateY(-1px)',
            },
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        };
    }
  };

  const buttonConfig = getButtonConfig();

  return {
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.2s ease-in-out',
    boxShadow: 'none',
    ...buttonConfig,
  };
});

const EasyCheckToggleButton = ({
  value,
  onChange,
  options = [],
  size = 'medium',
  buttonType = 'default',
  disabled = false,
  exclusive = true,
  orientation = 'horizontal',
  className,
  sx,
  ...props
}) => {
  return (
    <StyledToggleButtonGroup
      value={value}
      exclusive={exclusive}
      onChange={onChange}
      size={size}
      orientation={orientation}
      disabled={disabled}
      className={className}
      sx={sx}
      buttonType={buttonType}
      {...props}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          startIcon={option.icon}
          disabled={option.disabled}
        >
          {option.label}
        </ToggleButton>
      ))}
    </StyledToggleButtonGroup>
  );
};

EasyCheckToggleButton.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  buttonType: PropTypes.oneOf(['action', 'form', 'default']),
  disabled: PropTypes.bool,
  exclusive: PropTypes.bool,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  className: PropTypes.string,
  sx: PropTypes.object,
};

export default EasyCheckToggleButton; 